import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import SavedViewsBar from "@/components/SavedViewsBar";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, DownloadSimple } from "@phosphor-icons/react";
import LeadsFilters from "@/components/leads/LeadsFilters";
import LeadsTable from "@/components/leads/LeadsTable";
import LeadsBulkActions from "@/components/leads/LeadsBulkActions";
import { canGetConsentProof } from "@/components/leads/ConsentProofButton";
import { downloadLeadsExport, rollingDateRange } from "@/components/leads/leadsHelpers";

/**
 * `Leads` orchestrates filters + table + bulk actions. The heavy lifting
 * (UI markup, export blob handling) lives in the sub-components under
 * `components/leads/`.
 */
export default function Leads() {
  const { user, hasPerm } = useAuth();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [status, setStatus] = useState(params.get("status") || "");
  const [deliveryStatus, setDeliveryStatus] = useState(params.get("delivery_status") || "");
  const [clientId, setClientId] = useState(params.get("client_id") || "");
  const [affiliateId, setAffiliateId] = useState(params.get("affiliate_id") || "");
  const [state, setState] = useState(params.get("state") || "");
  const [isTest, setIsTest] = useState(params.get("is_test") || "");
  const [deliveredTo, setDeliveredTo] = useState(params.get("delivered_to") || "");
  const [landingPage, setLandingPage] = useState(params.get("landing_page") || "");
  const [dateFrom, setDateFrom] = useState(params.get("date_from") || "");
  const [dateTo, setDateTo] = useState(params.get("date_to") || "");
  // Iter-63.1: archived filter — Active (default), Archived, All.
  const [archived, setArchived] = useState(params.get("archived") || "active");
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [selected, setSelected] = useState({});

  const isInternal = user?.org_type === "internal";
  const canExport = user?.permissions?.includes("export_leads") || user?.role === "super_admin";
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  useEffect(() => {
    if (!isInternal) return;
    api.get("/organizations?type=client").then((r) => setClients(r.data.organizations));
    api.get("/organizations?type=affiliate").then((r) => setAffiliates(r.data.organizations));
  }, [isInternal]);

  // Assemble the current filter set into a URLSearchParams object.
  // Date range is sent as ISO-8601 (UTC start/end of the picked day).
  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (status) p.set("status", status);
    if (deliveryStatus) p.set("delivery_status", deliveryStatus);
    if (clientId) p.set("client_id", clientId);
    if (affiliateId) p.set("affiliate_id", affiliateId);
    if (state) p.set("state", state);
    if (isTest) p.set("is_test", isTest);
    if (deliveredTo) p.set("delivered_to", deliveredTo);
    if (landingPage.trim()) p.set("landing_page", landingPage.trim().toLowerCase());
    if (dateFrom) p.set("date_from", `${dateFrom}T00:00:00Z`);
    if (dateTo) p.set("date_to", `${dateTo}T23:59:59Z`);
    // Iter-63.1: forward the archived filter so the API returns the
    // right slice. Default `active` matches the pre-Iter-63.1 UX.
    p.set("archived", archived || "active");
    p.set("limit", "100");
    return p;
  }, [q, status, deliveryStatus, clientId, affiliateId, state, isTest, deliveredTo, landingPage, dateFrom, dateTo, archived]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/leads?${query.toString()}`);
      setLeads(r.data.leads);
      setTotal(r.data.total);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { load(); }, [load]);

  const applyToUrl = () => setParams(query);

  const applySavedFilters = (f) => {
    setQ(f.q || "");
    setStatus(f.status || "");
    setDeliveryStatus(f.delivery_status || "");
    setClientId(f.client_id || "");
    setAffiliateId(f.affiliate_id || "");
    setState(f.state || "");
    setIsTest(f.is_test || "");
    setDeliveredTo(f.delivered_to || "");
    setLandingPage(f.landing_page || "");
    setDateFrom(f.date_from ? f.date_from.slice(0, 10) : "");
    setDateTo(f.date_to ? f.date_to.slice(0, 10) : "");
    toast.success("View applied");
  };

  const onExport = (fmt, onlySelected) =>
    downloadLeadsExport(fmt, query, onlySelected ? selectedIds : null);

  const onDateRangePreset = (days) => {
    const { from, to } = rollingDateRange(days);
    setDateFrom(from);
    setDateTo(to);
  };

  const runBulk = async (action) => {
    if (!selectedIds.length) return toast.error("Select at least one lead");
    if (!confirm(`Apply "${action}" to ${selectedIds.length} leads?`)) return;
    try {
      const { data } = await api.post("/bulk/leads", { action, lead_ids: selectedIds });
      toast.success(`Updated ${data.updated}`);
      setSelected({});
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  // Iter-63.1: bulk archive / restore via the additive lifecycle routes.
  const bulkArchive = async () => {
    if (!selectedIds.length) return toast.error("Select at least one lead");
    if (!confirm(`Archive ${selectedIds.length} leads? Archived leads are hidden from Active views but keep every downstream record.`)) return;
    try {
      const { data } = await api.post("/leads/bulk-archive", { ids: selectedIds });
      toast.success(`Archived ${data.archived || 0}${data.already_archived ? ` · ${data.already_archived} already archived` : ""}`);
      setSelected({});
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Archive failed");
    }
  };
  const bulkRestore = async () => {
    if (!selectedIds.length) return toast.error("Select at least one lead");
    if (!confirm(`Restore ${selectedIds.length} leads back into Active views?`)) return;
    try {
      const { data } = await api.post("/leads/bulk-restore", { ids: selectedIds });
      toast.success(`Restored ${data.restored || 0}`);
      setSelected({});
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    }
  };

  const currentFilters = {
    q, status, delivery_status: deliveryStatus,
    client_id: clientId, affiliate_id: affiliateId, state, is_test: isTest,
    delivered_to: deliveredTo, landing_page: landingPage,
    date_from: dateFrom ? `${dateFrom}T00:00:00Z` : "",
    date_to: dateTo ? `${dateTo}T23:59:59Z` : "",
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-mono">Leads</div>
          <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">
            All leads
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {canExport && (
            <>
              <button onClick={() => onExport("csv", false)} data-testid="leads-export-csv"
                className="border-2 border-black px-3 py-2 text-xs uppercase tracking-widest hover:bg-black hover:text-white transition-colors duration-100 inline-flex items-center gap-2">
                <DownloadSimple size={14} weight="bold" /> Export CSV
              </button>
              <button onClick={() => onExport("xlsx", false)} data-testid="leads-export-xlsx"
                className="border-2 border-black px-3 py-2 text-xs uppercase tracking-widest hover:bg-black hover:text-white transition-colors duration-100 inline-flex items-center gap-2">
                <DownloadSimple size={14} weight="bold" /> Export XLSX
              </button>
            </>
          )}
          {isInternal && (
            <Link to="/leads/new" data-testid="new-lead-link"
              className="bg-black text-white px-3 py-2 text-xs uppercase tracking-widest border border-black hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100 inline-flex items-center gap-2">
              <Plus size={14} weight="bold" /> New lead
            </Link>
          )}
        </div>
      </div>

      <SavedViewsBar viewType="leads" currentFilters={currentFilters} onApply={applySavedFilters} />

      <LeadsFilters
        q={q} setQ={setQ}
        status={status} setStatus={setStatus}
        deliveryStatus={deliveryStatus} setDeliveryStatus={setDeliveryStatus}
        clientId={clientId} setClientId={setClientId}
        affiliateId={affiliateId} setAffiliateId={setAffiliateId}
        state={state} setState={setState}
        isTest={isTest} setIsTest={setIsTest}
        deliveredTo={deliveredTo} setDeliveredTo={setDeliveredTo}
        landingPage={landingPage} setLandingPage={setLandingPage}
        archived={archived} setArchived={setArchived}
        dateFrom={dateFrom} setDateFrom={setDateFrom}
        dateTo={dateTo} setDateTo={setDateTo}
        clients={clients} affiliates={affiliates}
        isInternal={isInternal}
        onApply={applyToUrl}
        onDateRangePreset={onDateRangePreset}
      />

      {selectedIds.length > 0 && (
        <div className="mt-3 flex justify-end">
          <LeadsBulkActions
            selectedCount={selectedIds.length}
            selectedIds={selectedIds}
            canExport={canExport}
            isInternal={isInternal}
            onExport={onExport}
            onBulk={runBulk}
            archived={archived}
            onArchive={bulkArchive}
            onRestore={bulkRestore}
            onMarked={async () => { setSelected({}); await load(); }}
          />
        </div>
      )}

      <LeadsTable
        leads={leads}
        total={total}
        loading={loading}
        selected={selected}
        setSelected={setSelected}
        showCheckbox={isInternal || canExport || canGetConsentProof(user, hasPerm)}
      />
    </div>
  );
}
